// Pickers for the stream forms: connection, ad accounts, and what to extract.

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Field, LoadingState, PLATFORMS } from './ui.jsx';
import { labelForKpi, labelForLevel, liveAccountLabel, useAdAccounts, useCapabilities, useConnections } from '../hooks.js';
import { selectMatchingAccounts, setupFieldChoices } from '../streamSetup.js';

const FALLBACK_TIMEZONES = [
  'UTC', 'Africa/Cairo', 'Africa/Johannesburg', 'America/Chicago', 'America/Los_Angeles',
  'America/New_York', 'America/Sao_Paulo', 'Asia/Beirut', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Riyadh', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney', 'Europe/Berlin',
  'Europe/London', 'Europe/Paris',
];

function timezoneOffset(timezone) {
  try {
    const raw = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value || 'GMT';
    return raw === 'GMT' ? 'UTC+00:00' : raw.replace('GMT', 'UTC');
  } catch {
    return 'UTC+00:00';
  }
}

function offsetMinutes(label) {
  const match = label.match(/UTC([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;
  const minutes = (Number(match[2]) * 60) + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

const TIMEZONES = [...new Set([
  'UTC',
  ...(typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : FALLBACK_TIMEZONES),
])]
  .map(id => ({ id, offset: timezoneOffset(id) }))
  .sort((left, right) => offsetMinutes(left.offset) - offsetMinutes(right.offset)
    || left.id.localeCompare(right.id));

export function TimezoneSelect({ value, onChange }) {
  const options = TIMEZONES.some(item => item.id === value)
    ? TIMEZONES
    : [{ id: value, offset: timezoneOffset(value) }, ...TIMEZONES];

  return <>
    <select className="o-control" value={value} onChange={event => onChange(event.target.value)} aria-label="Timezone">
      {options.map(timezone => (
        <option key={timezone.id} value={timezone.id}>
          {timezone.offset} · {timezone.id.replaceAll('_', ' ')}
        </option>
      ))}
    </select>
    <p className="o-note" style={{ margin: '8px 0 0' }}>The offset shown is current; scheduled times follow daylight-saving changes automatically.</p>
  </>;
}

/**
 * Searchable field selection. Setup treats empty as all supported fields; compact
 * export controls use explicit selections and can supply their own empty-state copy.
 */
export function useRemoteOptions(open, search, loadOptions, loadKey) {
  const loader = useRef(loadOptions);
  loader.current = loadOptions;
  const sequence = useRef(0);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ options: [], next: null, loading: false, error: '' });
  useEffect(() => {
    const request = ++sequence.current;
    if (!open || !loader.current) return undefined;
    setState({ options: [], next: null, loading: true, error: '' });
    const timer = window.setTimeout(async () => {
      try {
        const result = await loader.current(search, '');
        if (request === sequence.current) setState({ ...result, loading: false, error: '' });
      } catch (error) { if (request === sequence.current) setState({ options: [], next: null, loading: false, error: error.message || 'Values could not be loaded.' }); }
    }, 250);
    return () => { ++sequence.current; window.clearTimeout(timer); };
  }, [open, search, loadKey, Boolean(loadOptions), attempt]);
  const more = async () => {
    if (state.loading || !state.next) return;
    const request = sequence.current;
    setState(current => ({ ...current, loading: true, error: '' }));
    try {
      const result = await loader.current(search, state.next);
      if (request === sequence.current) setState(current => ({ ...result, options: [...current.options, ...result.options], loading: false, error: '' }));
    } catch (error) { if (request === sequence.current) setState(current => ({ ...current, loading: false, error: error.message })); }
  };
  return { ...state, more, retry: () => setAttempt(value => value + 1) };
}

function SelectedFieldChips({ label, options, selected, selectedLabels = {}, onRemove }) {
  return selected.map(id => {
    const option = options.find(option => option.id === id);
    const name = option?.label || selectedLabels[id] || String(id);
    return <span className="o-chip reach-field-chip" key={id} title={name}>
      <span>{name}</span>{!option?.locked && <button type="button" aria-label={`Remove ${name} from ${label.toLowerCase()}`}
        onClick={() => onRemove(id)}>×</button>}
    </span>;
  });
}

export function FieldDropdown({ label, options, selected = [], selectedLabels = {}, onChange, compact = false, chips = false, active = true, placeholder, emptyLabel, emptyHint, loadOptions, loadKey = '' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuLayout, setMenuLayout] = useState({ above: false, height: 360 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const dialogId = useId();
  const close = () => { setOpen(false); triggerRef.current?.focus(); };
  const remote = useRemoteOptions(open && active, search, loadOptions, loadKey);
  const choices = loadOptions ? remote.options : options;

  useEffect(() => { if (!active) setOpen(false); }, [active]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const position = () => {
      const bounds = rootRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const below = window.innerHeight - bounds.bottom - 16;
      const above = bounds.top - 16;
      const flip = below < 260 && above > below;
      setMenuLayout({ above: flip, height: Math.max(160, Math.min(360, flip ? above : below)),
        top: flip ? bounds.top - 4 : bounds.bottom + 4, left: bounds.left, width: bounds.width });
    };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [open, selected.length, chips]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = event => {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  const visible = choices.filter(option => option.label.toLowerCase().includes(search.trim().toLowerCase()));
  const editableSelected = selected.filter(id => !options.find(option => option.id === id)?.locked);
  const summary = selected.length
    ? selected.map(id => options.find(option => option.id === id)?.label || id).join(', ')
    : emptyLabel || (compact ? `Select ${label.toLowerCase()}` : `All supported ${label.toLowerCase()}`);
  const allSelected = visible.length > 0 && visible.every(option => selected.includes(option.id));
  const menu = open && active && <div ref={menuRef} id={dialogId} className="o-popover reach-field-menu" role="dialog" aria-label={`Choose ${label.toLowerCase()}`}
      data-reach-export-menu={chips || undefined}
      style={chips ? { position: 'fixed', zIndex: 160, width: menuLayout.width, left: menuLayout.left,
        top: menuLayout.above ? 'auto' : menuLayout.top, maxHeight: menuLayout.height,
        bottom: menuLayout.above ? window.innerHeight - menuLayout.top : undefined }
        : { maxHeight: menuLayout.height, top: menuLayout.above ? 'auto' : undefined,
          bottom: menuLayout.above ? 'calc(100% + var(--s1))' : undefined }}>
      <input type="search" className="o-control" aria-label={`Search ${label.toLowerCase()}`}
        placeholder={`Search ${label.toLowerCase()}…`} value={search} autoFocus
        onChange={event => setSearch(event.target.value)} />
      <p className="o-note">{editableSelected.length ? `${editableSelected.length} selected` : emptyHint || (compact ? 'None selected.' : 'No restriction — all available fields will be extracted.')}</p>
      {compact && <label className="reach-field-option reach-field-all">
        <input type="checkbox" aria-label={`Select all ${label.toLowerCase()}`} checked={allSelected}
          onChange={() => onChange(allSelected ? selected.filter(id => !visible.some(item => item.id === id)) : [...new Set([...selected, ...visible.map(option => option.id)])])} />
        <span>{loadOptions ? 'Select matches' : 'Select all'}</span>
      </label>}
      <div className="reach-field-options">
        {visible.map(option => <label key={option.id} className="reach-field-option">
          <input type="checkbox" checked={selected.includes(option.id)} disabled={option.locked} onChange={() => onChange(
            selected.includes(option.id) ? selected.filter(id => id !== option.id) : [...selected, option.id],
          )} />
          <span>{option.label}</span>
        </label>)}
        {!visible.length && !remote.loading && !remote.error && <div className="o-state">No fields match. Change the search term.</div>}
        {remote.loading && <p className="o-note" role="status">Loading values…</p>}
        {remote.error && <div className="o-hint o-error" role="alert">{remote.error}<button type="button" className="o-btn o-btn-sm" onClick={remote.retry}>Try again</button></div>}
        {remote.next && <button type="button" className="o-btn o-btn-sm" disabled={remote.loading} onClick={remote.more}>Load more values</button>}
      </div>
      {!compact && <div className="reach-field-menu-footer">
        <button type="button" className="o-btn o-btn-ghost o-btn-sm" disabled={!editableSelected.length} onClick={() => onChange(options.filter(option => option.locked).map(option => option.id))}>Use all</button>
        <button type="button" className="o-btn o-btn-sm" onClick={close}>Done</button>
      </div>}
    </div>;

  return <div className="reach-field-dropdown" ref={rootRef} onKeyDown={event => {
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); close(); }
  }}>
    {chips ? <div className="o-control reach-field-chip-control">
      <SelectedFieldChips label={label} options={options} selected={selected} selectedLabels={selectedLabels}
        onRemove={id => { onChange(selected.filter(value => value !== id)); triggerRef.current?.focus(); }} />
      <button ref={triggerRef} type="button" className="reach-field-chip-open" aria-label={label}
        aria-haspopup="dialog" aria-controls={dialogId} aria-expanded={open} disabled={!options.length && !loadOptions}
        onClick={() => { setSearch(''); setOpen(current => !current); }}>
        <span>{editableSelected.length ? placeholder || label : emptyLabel || placeholder || `Select ${label.toLowerCase()}`}</span><span aria-hidden="true">⌄</span>
      </button>
    </div> : <button ref={triggerRef} type="button" className="o-control reach-field-trigger"
      aria-label={label} aria-haspopup="dialog" aria-controls={dialogId} aria-expanded={open}
      disabled={!options.length && !loadOptions}
      title={summary} onClick={() => { setSearch(''); setOpen(current => !current); }}>
      <span>{summary}</span><span aria-hidden="true">⌄</span>
    </button>}
    {chips && menu ? createPortal(menu, document.body) : menu}
  </div>;
}

export function ExtractionPicker({ platform, levels, kpis, onChange, onLoadingChange, active = true, basic = false }) {
  const caps = useCapabilities(platform);
  const fields = basic ? setupFieldChoices(caps.levels, caps.kpis) : caps;
  const unsupported = [
    ...(levels || []).filter(id => !fields.levels.includes(id)).map(labelForLevel),
    ...(kpis || []).filter(id => !fields.kpis.includes(id)).map(labelForKpi),
  ];
  const unavailable = caps.loading || !!caps.error || !caps.known || unsupported.length > 0;

  useEffect(() => {
    onLoadingChange?.(unavailable);
  }, [unavailable, onLoadingChange]);

  if (caps.loading) return <LoadingState title="Loading available fields…" detail="Reading the dimensions and metrics supported by this platform." />;

  if (caps.error || !caps.known) return <div className="o-hint" role="alert">
    <b>Available fields could not be loaded.</b> {caps.error || 'No extractor is registered for this platform.'}
    <p className="o-note">Load the platform&apos;s supported fields before saving this stream.</p>
    <button type="button" className="o-btn o-btn-sm" onClick={caps.retry}>Try again</button>
  </div>;

  return (
    <div className="reach-extraction-fields">
      <Field label="Dimensions">
        <FieldDropdown label="Dimensions" chips active={active} placeholder="Dimensions" emptyLabel="All available dimensions"
          options={[...(basic ? [{ id: 'date', label: 'Day', locked: true }] : []), ...fields.levels.map(id => ({ id, label: basic && id === 'account' ? 'Account name' : labelForLevel(id) }))]}
          selected={basic ? ['date', ...(levels || [])] : levels || []} onChange={next => onChange({ levels: next.filter(id => id !== 'date'), kpis: kpis || [] })} />
        <p className="o-note">{basic ? 'Day is always included. Leave the other choices empty to extract Account name and Campaign where supported, including their IDs.' : 'Each reporting level includes its name and ID, in separate rows. Leave empty to extract every supported level.'}</p>
      </Field>
      <Field label="Metrics">
        <FieldDropdown label="Metrics" chips active={active} placeholder="Metrics" emptyLabel="All available metrics" options={fields.kpis.map(id => ({ id, label: labelForKpi(id) }))}
          selected={kpis || []} onChange={next => onChange({ levels: levels || [], kpis: next })} />
        <p className="o-note">Leave empty to extract every metric available in this dropdown.</p>
      </Field>
      {unsupported.length > 0 && <div className="o-hint"><b>Unsupported fields:</b> {unsupported.join(', ')}. Choose supported fields or use all.</div>}
    </div>
  );
}


/**
 * Pick which agency email a stream extracts with.
 *
 * A select rather than a chipset: there are about seven of these and the choice is one-of, where
 * the ad accounts below are many-of. Matching the control to the cardinality is what stops the
 * two steps looking like the same question asked twice.
 */
export function ConnectionPicker({ platform, value, onChange }) {
  const { loading, connections, error } = useConnections(platform);

  if (loading) return <LoadingState title="Loading connections…" detail="Checking the active connections for this platform." rows={2} style={{ maxWidth: 420 }} />;
  if (error) return <div className="o-hint"><b>Connections could not be read.</b> {error}</div>;
  if (!connections.length) return <div className="o-hint"><b>No {PLATFORMS.find(item => item.id === platform)?.label || 'platform'} email is connected.</b> An administrator connects one on the Connections tab; until then there is no token to extract with.</div>;

  return <label style={{ display: 'block', maxWidth: 420 }}>
    <span className="o-field-lbl">Connection</span>
    <select className="o-control" value={value} onChange={event => onChange(event.target.value)} style={{ marginTop: 8 }}>
      <option value="">Select a connected email</option>
      {connections.map(connection => <option key={connection.id} value={connection.id}>{connection.label}</option>)}
    </select>
  </label>;
}


/**
 * Searchable multi-select for the ad accounts a connection can see.
 *
 * The no-connection case is the common one, and it is the reason this is a component rather than
 * three lines inline: an empty chipset with no explanation reads as a broken screen, when what
 * it means is "choose a connection first".
 */
export function AdAccountPicker({ connectionId, selected, onToggle, onAccountsLoaded }) {
  const { loading, accounts, error } = useAdAccounts(connectionId);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('all');
  const [visibleLimit, setVisibleLimit] = useState(100);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const dialogId = useId();
  const close = () => { setOpen(false); triggerRef.current?.focus(); };

  useEffect(() => {
    setOpen(false);
    setSearch('');
    setView('all');
    setVisibleLimit(100);
  }, [connectionId]);

  useEffect(() => {
    if (!loading && !error) onAccountsLoaded?.(accounts);
  }, [accounts, error, loading, onAccountsLoaded]);

  useEffect(() => {
    setVisibleLimit(100);
  }, [search, open]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open]);

  if (!connectionId) return <div className="o-hint"><b>No connection chosen.</b> Go back a step and pick the email this stream extracts with.</div>;
  if (loading) return <LoadingState title="Loading ad accounts…" detail="Reading the accounts available through this connection. This may take a few seconds." />;
  if (error) return <div className="o-hint"><b>No ad accounts available.</b> {error}</div>;
  if (!accounts.length) return <div className="o-hint"><b>This email can see no ad accounts.</b> Check that the client granted it access in Business Manager.</div>;

  const query = search.trim().toLowerCase();
  const visible = accounts.filter(account => (view === 'all' || selected.includes(account.external_id))
    && liveAccountLabel(account).toLowerCase().includes(query));
  const rendered = visible.slice(0, visibleLimit);
  const visibleIds = visible.map(account => account.external_id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.includes(id));
  const selectedAccounts = selected.map(id => {
    const account = accounts.find(item => item.external_id === id);
    return { id, label: account ? liveAccountLabel(account) : id };
  });
  const toggleAccount = id => onToggle(current => current.includes(id)
    ? current.filter(item => item !== id)
    : [...current, id]);

  const selectVisible = () => onToggle(current => selectMatchingAccounts(current, visibleIds));

  return <div ref={rootRef} style={{ position: 'relative', width: 'min(560px, 100%)' }} onKeyDown={event => {
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); close(); }
  }}>
    <span className="o-field-lbl">Ad accounts</span>
    <div className="o-control reach-field-chip-control">
      <SelectedFieldChips label="Ad accounts" options={selectedAccounts} selected={selected}
        onRemove={id => { toggleAccount(id); triggerRef.current?.focus(); }} />
      <button ref={triggerRef}
      type="button"
      className="reach-field-chip-open"
      aria-label="Ad accounts"
      aria-haspopup="dialog"
      aria-controls={dialogId}
      aria-expanded={open}
      onClick={() => setOpen(current => !current)}
    >
      <span>{selected.length ? `${selected.length} selected` : 'Select ad accounts'}</span>
      <span aria-hidden="true">⌄</span>
    </button>
    </div>

    {open && <div id={dialogId} className="o-popover" role="dialog" aria-label="Choose ad accounts" style={{ position: 'absolute', top: 'calc(100% + var(--s1))', left: 0, zIndex: 80, width: '100%', padding: 'var(--s3)' }}>
      <input
        type="search"
        className="o-control"
        value={search}
        onChange={event => setSearch(event.target.value)}
        placeholder="Search account name or ID…"
        aria-label="Search ad accounts"
        autoFocus
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '10px 0 8px', flexWrap: 'wrap' }}>
        <div className="reach-account-tabs" aria-label="Account view">
          <button type="button" className="o-btn o-btn-sm" aria-pressed={view === 'all'} onClick={() => setView('all')}>All</button>
          <button type="button" className="o-btn o-btn-sm" aria-pressed={view === 'selected'} onClick={() => setView('selected')}>Selected ({selected.length})</button>
        </div>
        <button type="button" className="o-btn o-btn-ghost o-btn-sm" disabled={!visible.length || allVisibleSelected} onClick={selectVisible}>Select all</button>
      </div>

      <div className="o-note" style={{ marginBottom: 8 }}>{visible.length} matches · {accounts.length} available · {selected.length} selected. Select all applies to these matches.</div>

      <div role="listbox" aria-label="Ad accounts" aria-multiselectable="true" style={{ maxHeight: 260, overflowY: 'auto', borderTop: '1px solid var(--rule)', borderBottom: '1px solid var(--rule)' }}>
        {visible.length === 0
          ? <div className="o-state"><b>No accounts match.</b> Change the search term.</div>
          : rendered.map(account => {
            const checked = selected.includes(account.external_id);
            return <label key={account.external_id} role="option" aria-selected={checked} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px', cursor: 'pointer', borderBottom: '1px solid var(--rule-soft)' }}>
              <input type="checkbox" checked={checked} onChange={() => toggleAccount(account.external_id)} />
              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{liveAccountLabel(account)}</span>
            </label>;
          })}
      </div>

      {rendered.length < visible.length && (
        <button type="button" className="o-btn o-btn-ghost o-btn-sm" style={{ marginTop: 8 }} onClick={() => setVisibleLimit(current => current + 100)}>
          Show {Math.min(100, visible.length - rendered.length)} more
        </button>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 10 }}>
        <button type="button" className="o-btn o-btn-ghost o-btn-sm" disabled={selected.length === 0} onClick={() => onToggle([])}>Clear all</button>
        <button type="button" className="o-btn o-btn-sm" onClick={close}>Done</button>
      </div>
    </div>}
  </div>;
}

